/**
 * LEAF MODULE — this file must never import anything.
 *
 * Every value here is derived at module scope from a vite `define`. If this module ever
 * takes part in an import cycle, `apiUrl` can evaluate to `undefined` before initialization.
 * In dev that is a loud TDZ ReferenceError; in a production build it is SILENT — the app
 * degrades to permanent local-preview mode, looks perfectly healthy, and stops reaching AWS
 * on every run. Keep the import list empty. Imports point *at* this file, never out of it.
 */

/** `command === "build"` at compile time. Not the same thing as import.meta.env.PROD. */
export const isProductionBuild = __APP_IS_PRODUCTION_BUILD__;

export const configuredApiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "") || "";

/**
 * The backend origin, or "" in local preview.
 *
 * "" is a supported, first-class runtime: every screen must stay fully usable with no
 * backend. Callers must not hoist the `if (apiUrl)` branch out of the api layer.
 */
export const apiUrl = isProductionBuild ? configuredApiUrl : "";

export const googleClientId = __GOOGLE_CLIENT_ID__ || import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

export const googleAllowedDomain =
  __GOOGLE_ALLOWED_DOMAIN__ || import.meta.env.VITE_GOOGLE_ALLOWED_DOMAIN || "withmeridian.ai";

export const authRequired = isProductionBuild && Boolean(googleClientId);

/**
 * Are the Netlify functions (Gemini website research, MCP token) reachable?
 *
 * They are served in production, and locally ONLY under `netlify dev` — plain `vite dev` 404s on
 * /.netlify/functions/*. That 404 is not harmless: the browser logs it as a console error on every
 * lookup, which is real noise and which the smoke harness correctly fails on. So the call is gated
 * rather than attempted-and-caught.
 *
 * `npm run dev`          -> false: skip the call, fall back to local candidates (silent).
 * `npm run dev:functions` -> true : real Gemini, because netlify dev is serving the functions.
 */
export const functionsAvailable = __APP_FUNCTIONS_AVAILABLE__;

/**
 * The canonical public address of this app.
 *
 * The MCP endpoint shown in Settings and Help is a CONNECTION STRING the operator pastes into
 * Claude Desktop, ChatGPT or Cursor — so it must be the address those tools can actually reach,
 * not the origin the operator happens to be viewing from. Deriving it from
 * `window.location.origin` handed out `http://127.0.0.1:5175/mcp` on localhost (unreachable) and
 * an ephemeral `deploy-preview-*.netlify.app/mcp` on a preview — a URL that works the day you copy
 * it and is dead the week after. Override with VITE_APP_ORIGIN if the app is ever rehomed.
 */
export const appOrigin = (
  import.meta.env.VITE_APP_ORIGIN || "https://atlas-compiler.withmeridian.ai"
).replace(/\/$/, "");

export const sourceTextLimit = 900_000;
